import {
    Button,
    Card,
    Group,
    LoadingOverlay,
    Stack,
    Text
} from '@mantine/core';
import {
    PaymentElement,
    useElements,
    useStripe
} from '@stripe/react-stripe-js';
import {useState} from 'react';
import {showNotification} from '@mantine/notifications';
import {confirmPaymentProfileSetup} from '../../payment-profiles/services/PaymentProfilesService';

interface PaymentMethodSetupFormProps {
    customerId: string;
    billingProviderId: string;
    sessionId: string;
    onSuccess: () => void;
    onCancel: () => void;
}

export const PaymentMethodSetupForm = ({
                                           billingProviderId,
                                           sessionId,
                                           onSuccess,
                                           onCancel
                                       }: PaymentMethodSetupFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);

        try {
            const {error: submitError} = await elements.submit();
            if (submitError) {
                throw submitError;
            }

            const {error, setupIntent} = await stripe.confirmSetup({
                                                          elements,
                                                          confirmParams: {
                                                              return_url: window.location.origin,
                                                          },
                                                          redirect: 'if_required'
                                                      });

            if (error) {
                console.error('Setup intent confirmation error:', error);
                throw error;
            }

            console.log('Setup intent confirmed successfully:', setupIntent);
            console.log('Payment method ID:', setupIntent?.payment_method);

            await confirmPaymentProfileSetup({
                billingProviderId,
                sessionId,
                providerPaymentMethodId: setupIntent?.payment_method as string
            });

            showNotification({
                                 title: 'Payment Method Added',
                                 message: 'Your payment method has been saved successfully',
                                 color: 'teal',
                             });

            onSuccess();
        } catch (error) {
            showNotification({
                                 title: 'Setup Failed',
                                 message: error instanceof Error ? error.message : 'Failed to setup payment method',
                                 color: 'red',
                             });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Card withBorder p="xl" radius="md" style={{position: 'relative'}}>
            <LoadingOverlay visible={processing}/>

            <form onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}>
                <Stack gap="md">
                    <Text size="lg" fw={600}>Payment Method</Text>

                    <Text size="sm" c="dimmed">
                        Add a payment method for your subscription. This will be saved securely for future payments.
                    </Text>

                    <div style={{
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        backgroundColor: '#fff'
                    }}>
                        <PaymentElement/>
                    </div>

                    <Group justify="flex-end" mt="md">
                        <Button variant="subtle" onClick={onCancel} disabled={processing}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={processing} disabled={processing}>
                            Save Payment Method
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Card>
    );
};